import React, { createContext, useEffect, useState } from 'react';
import TagManager from 'react-gtm-module';
import { toaster } from 'evergreen-ui';
import { useParams } from 'react-router-dom';
import { useArrayState } from '../../../services/hooks/UseArrayState';
import {
    useValidatedState,
    MORE_THAN,
    LESS_THAN,
    NOT_NULL,
	IS_TRUE,
	EMPTY_URL,
} from '../../../services/hooks/UseValidatedState';
import BriefingProgress from '../../../components/Campanhas/BriefingProgress';
import Button from '../../../components/Campanhas/Button';
import { Loader } from '../../../components/Loader';
import Card from '../../../components/Card';
import BriefingStepObjective from '../../../components/Campanhas/BriefingSteps/step-1';
import BriefingStepValue from '../../../components/Campanhas/BriefingSteps/step-2';
import BriefingStepLogo from '../../../components/Campanhas/BriefingSteps/step-3';
import BriefingStepLocation from '../../../components/Campanhas/BriefingSteps/step-4';
import BriefingStepUrl from '../../../components/Campanhas/BriefingSteps/step-5';
import BriefingStepAuthorization from '../../../components/Campanhas/BriefingSteps/step-6';
import BriefingStepOverview from '../../../components/Campanhas/BriefingSteps/step-7';
import BriefingStepAcknowledment from '../../../components/Campanhas/BriefingSteps/step-8';
import api from '../../../services/api';
import { useHistoryPush } from '../../../services/hooks/UseHistoryPush';
import colors from '../../../config/colors.json';

export const BriefingContext = createContext(null);

export const ChannelConstants = {
    GOOGLE: 'google',
    FACEBOOK: 'facebook',
    INSTAGRAM: 'instagram',
};

const ValidChannels = [
    ChannelConstants.GOOGLE,
    ChannelConstants.FACEBOOK,
    ChannelConstants.INSTAGRAM,
];

export const ObjectivesContantsNames = [
	<>Campanha focada em gerar<br />leads para seus veículos.</>,
	<>Campanha focada em gerar<br />visitas para seu site.</>,
];

export const ObjectivesContantsSmallNames = [
	<>Promoção, estoque/serviços</>,
	<>Institucional/Branding</>,
];

export const ObjectivesContants = {
    RAPIDEZ: 0,
    SEGURANCA: 1,
};

const Briefing = ({ location }) => {
    const { briefingId } = location.state || {};
    const { channel } = useParams();
    const [step, setStep] = useState(1);
    const [url, setUrl, isValidUrl] = useValidatedState('', EMPTY_URL, true);
    const [campaignObjective, setCampaingObjective, isValidObjective] = useValidatedState(
        null,
        [
            NOT_NULL(),
            MORE_THAN(0, true),
            LESS_THAN(1, true),
        ],
        false,
    );
	const [campaignInvestment, setCampaignInvestment] = useState(800);
    const [idLogo, setIdLogo, isValidLogo] = useValidatedState(null, NOT_NULL);
    const [nameLogo, setNameLogo] = useState(null);
    const [campaignRange, addRange, removeRange, removeByCondition, editRange] = useArrayState([]);
    const [userAccepted, setUserAccepted, isValidAccepted] = useValidatedState(
        false,
        IS_TRUE,
        false,
    );
    const [loading, setLoading] = useState(false);
	const [fbtokenSent, setFbtokenSent] = useState(false);
    const [disabledButton, setDisabledButton] = useState(true);
    const totalSteps = channel === ChannelConstants.GOOGLE ? 6 : 7;
    const uppercaseChannel = channel.toUpperCase();
    const gtmBtnBaseName = `BTN-CAM-0${step}-${uppercaseChannel}`;

    const CANCEL_NM = 'CANCEL';
    const RETURN_NM = 'RETURN';
    const NEXT_NM = 'NEXT';
    const BUY_NM = 'BUY';

    const StepBackGtmEnum = {
        1: CANCEL_NM,
        2: RETURN_NM,
        3: RETURN_NM,
        4: RETURN_NM,
        5: RETURN_NM,
        6: RETURN_NM,
    };

    const StepNextGtmEnum = {
        1: NEXT_NM,
        2: null,
        3: null,
        4: null,
        5: NEXT_NM,
        6: channel === 'google' ? BUY_NM : NEXT_NM,
        7: channel !== 'google' && BUY_NM,
    };

    const actionTypesEnum = {
        back: StepBackGtmEnum[step],
        next: StepNextGtmEnum[step],
        location: 'ADDLOC',
        faceAuth: 'GRANT',
        termAccept: 'ACCEPT',
        termRefuse: 'REFUSE',
    };

    const StepsEnum = channel === ChannelConstants.GOOGLE
        ? {
            1: <BriefingStepObjective />,
            2: <BriefingStepLocation />,
            3: <BriefingStepLogo />,
            4: <BriefingStepValue />,
            5: <BriefingStepUrl />,
            6: <BriefingStepOverview />,
        } : {
            1: <BriefingStepObjective />,
            2: <BriefingStepLocation />,
            3: <BriefingStepLogo />,
            4: <BriefingStepValue />,
            5: <BriefingStepUrl />,
            6: <BriefingStepAuthorization />,
            7: <BriefingStepOverview />,
        };

    const FieldsPerStep = {
        1: [isValidObjective],
        2: [campaignRange.length > 0],
        3: [isValidLogo],
        4: [true],
        5: [isValidUrl || url === ''],
        6: channel === ChannelConstants.GOOGLE ? [isValidAccepted] : [fbtokenSent],
        7: [isValidAccepted],
        8: [true],
    };

    const Answers = [
        isValidObjective,
        isValidLogo,
        isValidUrl,
        isValidAccepted,
        campaignRange,
        fbtokenSent,
    ];

    useEffect(() => {
        if (channel === undefined || briefingId === undefined || !ValidChannels.includes(channel)) {
            useHistoryPush('/');
        }
    }, []);

    useEffect(() => {
        setDisabledButton(!FieldsPerStep[step].every(field => field));
    }, [step, ...Answers]);

    const finishCampaign = async () => api.put(`/api/v2/briefings/${briefingId}`, { finish: true },
    { validateStatus: () => true }).then(response => response.status === 200);

    const sendStepAnswers = data => api.put(`/api/v2/briefings/${briefingId}`, {
        answers: [data],
    }, {
        validateStatus: () => true,
    }).then((response) => {
        setLoading(false);
        if (response.status === 200) {
            return true;
        }
        return false;
    }).catch(() => false);

    const validateActualStep = async () => {
        switch (step) {
            case 1:
				if (!isValidObjective) {
					return false;
				}
                setDisabledButton(true);
                return sendStepAnswers({
                    alias: 'objective',
                    content: campaignObjective,
                });
            case 2:
                if (!FieldsPerStep[step].every(field => field === true)) {
                    toaster.danger('Todos os campos são obrigatórios.');
                    return false;
                }
                return sendStepAnswers({
                    alias: 'reach',
                    content: JSON.stringify(campaignRange.map(campaign => campaign.id)),
                });
            case 3:
                if (!FieldsPerStep[step].every(field => field === true)) {
                    toaster.danger('Todos os campos são obrigatórios.');
                    return false;
                }
                return sendStepAnswers({
                    alias: 'logo',
                    content: idLogo,
                });
            case 4:
                return sendStepAnswers({
                    alias: 'value',
                    content: campaignInvestment,
                });
            case 5:
                if (url.length === 0) {
                    return true;
                }
                if (!isValidUrl) {
                    return false;
                }
                return sendStepAnswers({
                    alias: 'target_link',
                    content: url,
                });
            default:
                return true;
        }
    };

    const sendGtmMetrics = (type) => {
        const info = actionTypesEnum[type];

        if (info !== null) {
            TagManager.dataLayer({
                dataLayer: {
                    event: 'trackEvent',
                    actionButton: `${gtmBtnBaseName}-${info}`,
                },
                dataLayerName: 'Campanhas',
            });
        }
    };

    const goToNextStep = async (finish) => {
        await sendGtmMetrics('next');
        setLoading(true);
        await validateActualStep()
        .then((response) => {
            if (response) {
                if (finish) {
                    finishCampaign();
                }
                return setStep((step < 8 ? step + 1 : step));
            }
            return false;
        });
        setLoading(false);
    };

    const goToPrevStep = async () => {
        await sendGtmMetrics('back');
        setStep((step > 1 ? step - 1 : useHistoryPush('/campanhas')));
    };

    const buttonNext = (content, finish = false) => (
	<Button
		id="campaign-next-step"
		type="button"
		padding="11px 18px"
		fontSize="12px"
		lineheight="16px"
		onClick={() => goToNextStep(finish)}
		disabled={disabledButton ? 'disabled' : ''}
		fontWeight="bold"
	>
		{loading ? <Loader /> : content}
	</Button>
    );

    const renderButton = () => {
        switch (step) {
            case 6:
                return buttonNext(channel === ChannelConstants.GOOGLE ? 'Contratar campanha' : 'Próximo', channel === ChannelConstants.GOOGLE);
            case 7:
                return buttonNext('Contratar campanha', true);
            default:
                return buttonNext('Próximo');
        }
    };

    const renderFormButtonRow = () => (
	<div style={{ display: 'flex', justifyContent: 'space-between' }}>
		<div style={{ width: '120px' }}>
			<Button
				id="campaign-prev-step"
				theme="grey1"
				type="button"
				fontSize="12px"
				lineheight="16px"
				onClick={goToPrevStep}
				fontWeight="bold"
				padding="11px 0"
			>
				{ step <= 1 ? 'Cancelar' : 'Voltar' }
			</Button>
		</div>
		<div style={{ minWidth: '120px' }}>
			{renderButton()}
		</div>
	</div>
    );

    return (
	<div className="row justify-content-center">
		<div className="col-12">
			<BriefingContext.Provider value={{
                        gtm: {
                            sendGtmMetrics,
                        },
                        urls: {
                            url,
							setUrl,
							isValidUrl,
                        },
                        campaign: {
                            userAccepted,
                            setUserAccepted,
                            campaignObjective,
                            setCampaingObjective,
                            campaignInvestment,
                            setCampaignInvestment,
                            fbtokenSent,
                            setFbtokenSent,
                            idLogo,
                            setIdLogo,
                            nameLogo,
                            setNameLogo,
                            channel,
                            campaignRange,
                            addRange,
                            removeRange,
                            removeByCondition,
                            editRange,
                            isValidUrl,
                            isValidObjective,
                            isValidLogo,
                        },
                    }}
			>
				<div style={{
                    width: '100%',
                    padding: '30px 15px',
                }}
				>
					<Card
						contentMargin="0px"
						boxShadow={`0 2px 2px ${colors.darkGrey3_15}`}
					>
						<div style={{ margin: '0px 15px' }}>
							<BriefingProgress step={step} channel={channel} totalSteps={totalSteps + 1} />
							{ StepsEnum[step] }
							<div
								style={{ marginTop: '30px' }}
							>
								{ step <= totalSteps ? renderFormButtonRow() : <BriefingStepAcknowledment />}
							</div>
						</div>
					</Card>
				</div>
			</BriefingContext.Provider>
		</div>
	</div>
    );
};

export default Briefing;
